import * as THREE from 'three'; // Keep if MobileController or other parts need it

export class InputController {
    // Pass the main game instance which holds references to other systems
    constructor(game) {
        this.game = game; // Store reference to the game object

        // Store currently pressed keys
        this.keys = {};

        // Gamepad state
        this.gamepad = null;
        this.gamepadState = null;
        this.gamepadType = 'generic';
        this.prevGamepadButtons = [];
        this.controllerDeadzone = 0.1;
        this.steeringSensitivity = 1.0;
        this.vibrationIntensity = 0.75;

        // Mobile controller state
        this.mobileController = null;
        this.isMobileDevice = this.detectMobileDevice();

        // Game state (can be useful)
        this.isGameOver = false;

        // --- REMOVED: No longer holds a direct weapon reference ---
        // this.currentWeapon = null;

        // Bind event handlers
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onGamepadConnected = this.onGamepadConnected.bind(this);
        this.onGamepadDisconnected = this.onGamepadDisconnected.bind(this);
        this.onMeteorWarning = this.onMeteorWarning.bind(this);
        this.onMeteorBusImpact = this.onMeteorBusImpact.bind(this);

        // Register event listeners
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);

        // Set up gamepad detection
        this.setupGamepadDetection();
        window.addEventListener('meteor_warning_started', this.onMeteorWarning);
        window.addEventListener('meteor_bus_impact', this.onMeteorBusImpact);
    }

    // Detect if the current device is a mobile device
    detectMobileDevice() {
        // Check for tablet devices specifically
        const isTablet = /iPad|Tablet|Android(?!.*Mobile)|Surface/i.test(navigator.userAgent);

        // Check for mobile devices
        const isMobile = /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Check screen size (tablets typically have larger screens)
        const isLargeScreen = window.matchMedia && window.matchMedia('(min-width: 600px) and (max-width: 1366px)').matches;

        return isTablet || isMobile || isLargeScreen;
    }

    // Initialize mobile controller if needed
    initMobileController() { // Removed game param, uses this.game
        if (this.isMobileDevice && !this.mobileController) {
            // Dynamically import the MobileController to avoid loading it on desktop
            import('../ui/MobileController.js').then(module => {
                const MobileController = module.default;
                // Pass the game reference to MobileController if it needs it
                this.mobileController = new MobileController(this.game);
                console.log('Mobile controller initialized');
            }).catch(error => {
                console.error('Failed to load mobile controller:', error);
            });
        }
    }

    onKeyDown(event) {
        // Check if welcome screen is visible
        const welcomeScreen = document.getElementById('welcome-screen');
        if (welcomeScreen && welcomeScreen.style.display !== 'none') {
            return; // Don't process keyboard events when welcome screen is visible
        }

        // Store the key state
        this.keys[event.code] = true; // Use event.code for consistency
        this.keys[event.key.toLowerCase()] = true; // Store lower-case key too

        // --- Weapon Controls ---
        if (event.code === 'Space') {
            // Tell the WeaponManager to fire the current weapon
            // Use optional chaining (?.) in case manager isn't ready yet
            this.game?.weaponManager?.fireCurrentWeapon();
            event.preventDefault(); // Prevent spacebar scrolling page
        } else if (event.code === 'KeyR') {
            // Tell the WeaponManager to reload
            this.game?.weaponManager?.reloadCurrentWeapon();
        } else if (event.code === 'Digit1') {
             this.game?.weaponManager?.switchWeapon('machineGun'); // Assuming 'machineGun' exists
        }
        // Add more digits/keys for other weapon types
        // else if (event.code === 'Digit2') {
        //     this.game?.weaponManager?.switchWeapon('rocketLauncher');
        // }

        // --- Other Controls ---
        // Your existing logic for 'c' or other keys can remain or be adapted
        // if (event.code === 'KeyC') {
        //     // Handle 'c' key press (e.g., change camera, special ability)
        // }
    }

    onKeyUp(event) {
        // Check if welcome screen is visible
        const welcomeScreen = document.getElementById('welcome-screen');
        if (welcomeScreen && welcomeScreen.style.display !== 'none') {
            return; // Don't process keyboard events when welcome screen is visible
        }

        // Update the key state
        this.keys[event.code] = false;
        this.keys[event.key.toLowerCase()] = false;
    }

    isKeyDown(keyOrCode) {
        // Check if a key (like 'w') or code (like 'KeyW' or 'Space') is currently pressed
        return this.keys[keyOrCode] === true;
    }

    // Added for compatibility with Bus.js - Forward Input
    getForwardInput() {
        if (this.isGameOver) return 0;
        let forward = 0;
        if (this.isKeyDown('w') || this.isKeyDown('ArrowUp') || this.isKeyDown('KeyW')) forward = 1;
        else if (this.isKeyDown('s') || this.isKeyDown('ArrowDown') || this.isKeyDown('KeyS')) forward = -1;

        if (this.gamepadState) {
            const triggerDrive = this.gamepadState.rightTrigger - this.gamepadState.leftTrigger;
            if (Math.abs(triggerDrive) > this.controllerDeadzone * 0.5) {
                forward = triggerDrive;
            } else if (Math.abs(this.gamepadState.leftStickY) > this.controllerDeadzone * 0.5) {
                forward = -this.gamepadState.leftStickY;
            }
        }
        if (this.mobileController) {
            const joystickInput = this.mobileController.getJoystickInput();
            if (Math.abs(joystickInput.y) > 0.1) forward = -joystickInput.y;
            if (this.mobileController.isBrakeActive()) forward = -1;
        }
        return forward;
    }

    // Added for compatibility with Bus.js - Right Input (Steering)
    getRightInput() {
         if (this.isGameOver) return 0;
        let right = 0;
        // REVERSED: D/Right turns left, A/Left turns right (adjust if this was intentional)
        if (this.isKeyDown('d') || this.isKeyDown('ArrowRight') || this.isKeyDown('KeyD')) right = -1;
        else if (this.isKeyDown('a') || this.isKeyDown('ArrowLeft') || this.isKeyDown('KeyA')) right = 1;

        if (this.gamepadState) {
            const rawRight = this.gamepadState.leftStickX ? -this.gamepadState.leftStickX : right;
            right = Math.max(-1, Math.min(1, rawRight * this.steeringSensitivity));
        }
        if (this.mobileController) {
            const joystickInput = this.mobileController.getJoystickInput();
            if (Math.abs(joystickInput.x) > 0.1) right = -joystickInput.x;
        }
        return right;
    }

    getBrakeInput() {
        let brake = (this.isKeyDown('s') || this.isKeyDown('S') || this.isKeyDown('KeyS')) ? 1 : 0;
        if (this.gamepadState) {
            brake = Math.max(brake, this.gamepadState.leftTrigger);
            if (this.gamepadState.crossPressed) brake = Math.max(brake, 1);
        }
        if (this.mobileController?.isBrakeActive()) {
            brake = 1;
        }
        return brake;
    }

    getHornInput() {
        let horn = this.isKeyDown('h') || this.isKeyDown('H') || this.isKeyDown('KeyH');
        if (this.gamepadState?.circlePressed) horn = true;
        if (this.mobileController?.isHornActive()) horn = true;
        return horn;
    }

    // Added for compatibility with Bus.js - General Key Press Check
    isKeyPressed(keyIdentifier) {
         // Primarily checks lower-case key or specific codes like 'Space'
         let isPressed = this.isKeyDown(keyIdentifier.toLowerCase()) || this.isKeyDown(keyIdentifier);

        // Mobile checks (only horn example shown)
        if (keyIdentifier.toLowerCase() === 'h' && this.mobileController && this.mobileController.isHornActive()) {
            isPressed = true;
        }

        // Add mobile checks for firing if needed
        // if (keyIdentifier === 'Space' && this.mobileController && this.mobileController.isFireButtonPressed()) {
        //    isPressed = true;
        // }

        return isPressed;
    }


    setupGamepadDetection() {
        window.addEventListener('gamepadconnected', this.onGamepadConnected);
        window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    }

    onGamepadConnected(e) {
        this.gamepad = e.gamepad;
        this.gamepadType = this.detectGamepadType(e.gamepad);
        this.prevGamepadButtons = [];
        console.log(`Gamepad connected: ${e.gamepad.id} (${this.gamepadType})`);
        window.dispatchEvent(new CustomEvent('controller_status_changed', {
            detail: { connected: true, type: this.gamepadType }
        }));
        this.game?.showNotificationMessage?.(
            this.gamepadType === 'dualsense' ? 'PS5 controller connected' : 'Controller connected',
            'rgba(40,100,190,0.85)'
        );
    }

    onGamepadDisconnected(e) {
        console.log(`Gamepad disconnected: ${e.gamepad.id}`);
        this.gamepad = null;
        this.gamepadState = null;
        this.prevGamepadButtons = [];
        window.dispatchEvent(new CustomEvent('controller_status_changed', {
            detail: { connected: false, type: 'none' }
        }));
    }

    detectGamepadType(gamepad) {
        const id = (gamepad?.id || '').toLowerCase();
        if (id.includes('dualsense') || id.includes('wireless controller')) {
            return 'dualsense';
        }
        return 'generic';
    }

    updateGamepadState() {
        const gamepads = navigator.getGamepads();
        if (!gamepads) { this.gamepadState = null; return; }

        if (!this.gamepad) {
            this.gamepad = Array.from(gamepads).find((gp) => gp && gp.connected) || null;
            if (!this.gamepad) {
                this.gamepadState = null;
                return;
            }
            this.gamepadType = this.detectGamepadType(this.gamepad);
        } else {
            this.gamepad = gamepads[this.gamepad.index];
        }

        if (!this.gamepad) return;

        const buttonPressed = (index) => !!this.gamepad.buttons[index]?.pressed;
        const edgePressed = (index) => {
            const nowPressed = buttonPressed(index);
            const wasPressed = !!this.prevGamepadButtons[index];
            return nowPressed && !wasPressed;
        };

        this.gamepadState = {
            leftStickX: this.applyDeadzone(this.gamepad.axes[0], this.controllerDeadzone),
            leftStickY: this.applyDeadzone(this.gamepad.axes[1], this.controllerDeadzone),
            rightStickX: this.applyDeadzone(this.gamepad.axes[2], this.controllerDeadzone),
            rightStickY: this.applyDeadzone(this.gamepad.axes[3], this.controllerDeadzone),
            leftTrigger: this.gamepad.buttons[6]?.value || 0,
            rightTrigger: this.gamepad.buttons[7]?.value || 0,
            crossPressed: buttonPressed(0),     // X on PS5
            circlePressed: buttonPressed(1),    // O on PS5
            squarePressed: buttonPressed(2),    // Square on PS5
            trianglePressed: buttonPressed(3),  // Triangle on PS5
            l1Pressed: buttonPressed(4),
            r1Pressed: buttonPressed(5),
            optionsPressed: buttonPressed(9)
        };

        // --- Controller actions ---
        if (this.gamepadState.r1Pressed) {
            this.game?.weaponManager?.fireCurrentWeapon();
        }
        if (edgePressed(2)) {
            this.game?.weaponManager?.reloadCurrentWeapon();
        }
        if (edgePressed(3)) {
            this.game?.cameraManager?.toggleCameraMode?.();
        }
        if (edgePressed(9)) {
            this.game?.uiManager?.toggleSettingsPanel?.();
        }

        this.prevGamepadButtons = this.gamepad.buttons.map((b) => !!b?.pressed);
    }

    applyDeadzone(value, deadzone) {
        if (Math.abs(value) < deadzone) return 0;
        return (value - Math.sign(value) * deadzone) / (1 - deadzone);
    }

    onMeteorWarning() {
        this.vibrateGamepad(150, 0.2, 0.5);
    }

    onMeteorBusImpact() {
        this.vibrateGamepad(210, 0.55, 1.0);
    }

    vibrateGamepad(durationMs = 150, weakMagnitude = 0.25, strongMagnitude = 0.6) {
        if (this.vibrationIntensity <= 0) return;

        const activePad = this.gamepad || (navigator.getGamepads ? Array.from(navigator.getGamepads() || []).find((gp) => gp && gp.connected) : null);
        if (!activePad) return;

        const weak = Math.max(0, Math.min(1, weakMagnitude * this.vibrationIntensity));
        const strong = Math.max(0, Math.min(1, strongMagnitude * this.vibrationIntensity));

        if (activePad.vibrationActuator?.playEffect) {
            activePad.vibrationActuator.playEffect('dual-rumble', {
                startDelay: 0,
                duration: durationMs,
                weakMagnitude: weak,
                strongMagnitude: strong
            }).catch(() => {});
            return;
        }

        const haptics = activePad.hapticActuators || [];
        if (haptics[0]?.pulse) {
            haptics[0].pulse(strong, durationMs).catch(() => {});
        }
    }

    setControllerDeadzone(deadzone = 0.1) {
        this.controllerDeadzone = Math.max(0, Math.min(0.35, deadzone));
    }

    setSteeringSensitivity(value = 1) {
        this.steeringSensitivity = Math.max(0.6, Math.min(1.8, value));
    }

    setVibrationIntensity(value = 0.75) {
        this.vibrationIntensity = Math.max(0, Math.min(1, value));
    }

    getControllerTuning() {
        return {
            deadzone: this.controllerDeadzone,
            sensitivity: this.steeringSensitivity,
            vibrationIntensity: this.vibrationIntensity
        };
    }

    getGamepadState() {
        // Update is handled in main update loop now
        return this.gamepadState;
    }

    // Renamed from 'update' to avoid clash if InputController itself needs per-frame logic unrelated to gamepad polling
    pollInputSources() {
        // Update gamepad state polling
        this.updateGamepadState();

        // --- Continuous Actions (like holding fire key) ---
        // Note: fireCurrentWeapon() itself handles fire rate limiting.
        if (this.isKeyDown('Space')) {
             this.game?.weaponManager?.fireCurrentWeapon();
        }

        // Update mobile controller state if it exists
        if (this.mobileController && typeof this.mobileController.update === 'function') {
            this.mobileController.update();
        }
    }

    cleanup() {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('gamepadconnected', this.onGamepadConnected);
        window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
        window.removeEventListener('meteor_warning_started', this.onMeteorWarning);
        window.removeEventListener('meteor_bus_impact', this.onMeteorBusImpact);
        this.mobileController?.cleanup();
    }
}
